import apiClient from '@/api/client';

// ============================================================
// Platform admin — /platform/ai-pricing
// ============================================================
export interface AIFeatureWeight {
  feature_code: string;
  display_name: string;
  description: string | null;
  credits: number;
  model_hint: string | null;
  platform_paid: boolean;
  is_active: boolean;
}

export interface AIFeatureWeightCreate {
  feature_code: string;
  display_name: string;
  credits: number;
  description?: string;
  model_hint?: string;
  platform_paid?: boolean;
}

export interface AIFeatureWeightUpdate {
  display_name?: string;
  description?: string | null;
  credits?: number;
  model_hint?: string;
  platform_paid?: boolean;
  is_active?: boolean;
}

export interface PlanAI {
  id: string;
  slug: string;
  name: string;
  ai_credits_monthly: number;
  ai_overage_allowed: boolean;
  ai_byok_allowed: boolean;
}

export interface PlanAIUpdate {
  ai_credits_monthly?: number;
  ai_overage_allowed?: boolean;
  ai_byok_allowed?: boolean;
}

export interface TenantUsageRow {
  group_id: string;
  group_name: string | null;
  plan_slug: string | null;
  credits_used: number;
  limit: number;
  overage_credits: number;
  byok_enabled: boolean;
}

export interface UsageOverviewResponse {
  period_days: number;
  tenants: TenantUsageRow[];
}

export interface PlanMarginRow {
  plan_id: string;
  plan_slug: string;
  plan_name: string;
  active_subs: number;
  base_price_monthly: number;
  mrr_cents: number;
  credits_used_period: number;
  overage_charged_cents: number;
  estimated_cost_cents: number;
  revenue_cents: number;
  margin_cents: number;
  margin_pct: number;
}

export interface PlanMarginResponse {
  cost_per_credit_cents: number;
  rows: PlanMarginRow[];
  totals: PlanMarginRow;
}

// ============================================================
// Tenant — /settings/ai
// ============================================================
export interface AIBillingStatus {
  plan_name: string | null;
  credits_used: number;
  limit: number; // 0 = unlimited
  remaining: number; // -1 when unlimited
  period_start: string | null;
  period_end: string | null;
  overage_allowed_by_plan: boolean;
  overage_optin: boolean;
  overage_cap_cents: number | null;
  overage_charged_cents: number;
  byok_allowed_by_plan: boolean;
  byok_enabled: boolean;
  byok_last4: string | null;
}

export interface LedgerEntry {
  feature_code: string;
  credits: number;
  calls: number;
}

export interface LedgerBreakdown {
  period_start: string | null;
  by_feature: LedgerEntry[];
}

export interface OverageUpdate {
  optin: boolean;
  cap_cents?: number | null;
}

export interface BYOKResponse {
  byok_enabled: boolean;
  last4: string | null;
}

// ============================================================
// API
// ============================================================
export const aiBillingApi = {
  // Platform admin
  listWeights: (includeInactive = false) =>
    apiClient.get<AIFeatureWeight[]>('/platform/ai-pricing/weights', {
      params: { include_inactive: includeInactive },
    }),
  createWeight: (body: AIFeatureWeightCreate) =>
    apiClient.post<AIFeatureWeight>('/platform/ai-pricing/weights', body),
  updateWeight: (feature_code: string, body: AIFeatureWeightUpdate) =>
    apiClient.patch<AIFeatureWeight>(
      `/platform/ai-pricing/weights/${feature_code}`,
      body,
    ),
  listPlansAI: () => apiClient.get<PlanAI[]>('/platform/ai-pricing/plans'),
  updatePlanAI: (plan_id: string, body: PlanAIUpdate) =>
    apiClient.patch<PlanAI>(`/platform/ai-pricing/plans/${plan_id}`, body),
  usageOverview: (days = 30) =>
    apiClient.get<UsageOverviewResponse>(
      '/platform/ai-pricing/usage-overview',
      { params: { days } },
    ),
  margin: () =>
    apiClient.get<PlanMarginResponse>('/platform/ai-pricing/margin'),

  // Tenant
  getStatus: () => apiClient.get<AIBillingStatus>('/settings/ai'),
  getLedger: () => apiClient.get<LedgerBreakdown>('/settings/ai/ledger'),
  updateOverage: (body: OverageUpdate) =>
    apiClient.post<AIBillingStatus>('/settings/ai/overage', body),
  submitByok: (raw_key: string) =>
    apiClient.post<BYOKResponse>('/settings/ai/byok', { raw_key }),
  clearByok: () => apiClient.delete<BYOKResponse>('/settings/ai/byok'),
};
